import React, { useEffect, useState } from 'react';
import { Link } from '../routing';
import { Sparkles, Mail, Instagram, MapPin, Clock, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import TraderIdentity from '../components/TraderIdentity';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import { useCompany } from '../utils/useCompany';
import theme from '../theme';

export default function Contact() {
  const { t } = useTranslation();
  // The support address is configuration, not a literal: the one hardcoded here
  // pointed at glamgo.cy, a domain with no MX record, so every enquiry bounced.
  const company = useCompany();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  // 'idle' | 'sending' | 'sent' | 'failed'. 'failed' is its own state rather
  // than a toast, because it is the one that has to keep the typed message on
  // screen and offer the fallback.
  const [state, setState] = useState('idle');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // For a while this form sent nothing at all. It handed the text to whatever
  // mail app the device had, and inside the Capacitor WebView — with no mail app
  // registered for mailto: — that hand-off did nothing, with no way for the page
  // to tell. An earlier version set `sent` unconditionally and showed a tick, so
  // a customer could believe support had their message when nothing had left the
  // phone; the version after that was honest about not knowing, which is better
  // but still leaves the only working channel as "copy this address by hand".
  //
  // POST /api/contact actually delivers, and answers whether it did. The mailto:
  // hand-off survives as the fallback for when it does not, together with the
  // text itself — a support form must never be the thing that loses the message.
  const composed = `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`;

  const mailtoHref = () => {
    const subject = encodeURIComponent(`GlamGo Enquiry from ${form.name}`);
    return `mailto:${company.email}?subject=${subject}&body=${encodeURIComponent(composed)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    try {
      await api.post('/contact', form);
      setState('sent');
    } catch (err) {
      // The interceptor flattens the body onto the error, so err.message is the
      // server's sentence when there is one.
      setState('failed');
      toast.error(err?.message || t('contact.sendFailed'), { duration: 8000 });
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(composed);
      toast.success(t('contact.copiedToast'));
    } catch (_) {
      // Clipboard access can be refused outright; the text is still on screen
      // to select by hand, so say that rather than claim a copy that failed.
      toast.error(t('contact.copyFailedToast'));
    }
  };

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      {/* Navbar */}
      <nav style={{ background: theme.espresso, padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <Sparkles size={18} color={theme.gold} />
          <span style={{ fontFamily: theme.font.display, fontSize: 24, fontWeight: 600, color: theme.white, letterSpacing: '0.05em' }}>
            GLAM<span style={{ color: theme.gold }}>GO</span>
          </span>
        </Link>
        <Link to="/" style={{ color: theme.alpha.white(0.6), textDecoration: 'none', fontSize: 12, letterSpacing: '0.12em' }}>{t('contact.back')}</Link>
      </nav>

      {/* Header */}
      <PageHero
        eyebrow={t('contact.label')}
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        height={320}
      />

      {/* Content */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '80px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 64 }}>
        {/* Info */}
        <Reveal>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, fontWeight: 400, margin: '0 0 40px' }}>{t('contact.reachOut')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <ContactItem icon={<Mail size={20} color={theme.gold} />} label={t('contact.email')} href={`mailto:${company.email}`}>
              {company.email}
            </ContactItem>
            {company.phone && (
              <ContactItem icon={<Phone size={20} color={theme.gold} />} label={t('contact.phone')} href={`tel:${company.phone.replace(/\s+/g, '')}`}>
                {company.phone}
              </ContactItem>
            )}
            <ContactItem icon={<Instagram size={20} color={theme.gold} />} label={t('contact.instagram')} href="https://instagram.com/glamgo.hair" external>
              @glamgo.hair
            </ContactItem>
            <ContactItem icon={<MapPin size={20} color={theme.gold} />} label={t('contact.location')}>
              {t('contact.locationValue')}<br />
              <span style={{ color: theme.grey400, fontSize: 13 }}>{t('contact.locationSub')}</span>
            </ContactItem>
            <ContactItem icon={<Clock size={20} color={theme.gold} />} label={t('contact.supportHours')}>
              {t('contact.hours')}<br />
              <span style={{ color: theme.grey400, fontSize: 13 }}>{t('contact.hoursSub')}</span>
            </ContactItem>
          </div>

          {/* Art. 5 of the E-Commerce Directive wants the trader identifiable on
              the site itself, not only inside the Terms. */}
          <div style={{ marginTop: 40, paddingTop: 28, borderTop: `1px solid ${theme.sand}` }}>
            <p style={{ fontSize: 11, letterSpacing: '0.14em', color: theme.grey400, margin: '0 0 10px' }}>{t('contact.traderDetails')}</p>
            <TraderIdentity style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.8, margin: 0 }} />
          </div>

          <div style={{ marginTop: 32, background: theme.alpha.gold(0.08), border: `1px solid ${theme.alpha.gold(0.25)}`, borderRadius: 4, padding: '24px 28px' }}>
            <p style={{ fontFamily: theme.font.display, fontSize: 18, color: theme.ink, fontWeight: 500, margin: '0 0 8px' }}>{t('contact.emergencyTitle')}</p>
            <p style={{ color: theme.grey600, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
              {t('contact.emergencyText')}
            </p>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.12}>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, fontWeight: 400, margin: '0 0 40px' }}>{t('contact.sendMessage')}</h2>
          {state === 'sent' ? (
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
              <p style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, marginBottom: 8 }}>{t('contact.sentTitle')}</p>
              {/* Says what happens next, and by when — Terms §15 puts a clock on
                  it, and this is where the clock starts. */}
              <p style={{ color: theme.grey500, fontSize: 14, lineHeight: 1.7, maxWidth: 420, margin: '0 auto 20px' }}>{t('contact.sentBody')}</p>
              <button onClick={() => { setForm({ name: '', email: '', message: '' }); setState('idle'); }}
                style={{ background: 'none', border: `1px solid ${theme.gold}`, color: theme.gold, padding: '10px 24px', borderRadius: 2, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
                {t('contact.sendAnother')}
              </button>
            </div>
          ) : state === 'failed' ? (
            <div style={{ padding: '24px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 16, textAlign: 'center' }}>✉️</div>
              <p style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, marginBottom: 8, textAlign: 'center' }}>{t('contact.failedTitle')}</p>
              <p style={{ color: theme.goldInkAlt, fontSize: 14, lineHeight: 1.7, background: theme.alpha.gold(0.10), border: `1px solid ${theme.alpha.gold(0.35)}`, borderRadius: 4, padding: '14px 16px', margin: '16px 0' }}>
                {t('contact.notSentWarning')}
              </p>
              <p style={{ color: theme.grey500, fontSize: 14, textAlign: 'center' }}>{t('contact.emailDirectNote')} <a href={`mailto:${company.email}`} style={{ color: theme.gold }}>{company.email}</a></p>
              {/* The typed message is kept on screen and copyable. A support
                  form that eats the message on failure is worse than no form. */}
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 16, fontSize: 13, color: theme.grey600, fontFamily: theme.font.body, margin: '16px 0' }}>{composed}</pre>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button onClick={copyMessage} style={{ background: theme.ink, border: 'none', color: theme.white, padding: '10px 24px', borderRadius: 2, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>{t('contact.copyMessage')}</button>
                <a href={mailtoHref()} style={{ background: theme.ink, color: theme.white, padding: '10px 24px', borderRadius: 2, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body, textDecoration: 'none', display: 'inline-block' }}>{t('contact.openMailApp')}</a>
                <button onClick={() => setState('idle')} style={{ background: 'none', border: `1px solid ${theme.gold}`, color: theme.gold, padding: '10px 24px', borderRadius: 2, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>{t('contact.tryAgain')}</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <Field label={t('contact.name')} value={form.name} onChange={v => setForm({ ...form, name: v })} required />
              <Field label={t('contact.email')} type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} required />
              <div>
                <label style={labelStyle}>{t('contact.message')}</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  required
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                  placeholder={t('contact.placeholder')}
                />
              </div>
              <button type="submit" disabled={state === 'sending'} style={{
                background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDeep})`,
                color: theme.white, border: 'none', padding: '16px 32px',
                borderRadius: 2, fontSize: 13, letterSpacing: '0.15em',
                cursor: state === 'sending' ? 'wait' : 'pointer',
                opacity: state === 'sending' ? 0.6 : 1,
                fontFamily: theme.font.body, fontWeight: 500
              }}>{state === 'sending' ? t('contact.sending') : t('contact.send')}</button>
            </form>
          )}
        </Reveal>
      </div>

    </div>
  );
}

function ContactItem({ icon, label, href, external, children }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 11, letterSpacing: '0.15em', color: theme.gold, marginBottom: 4, textTransform: 'uppercase' }}>{label}</div>
        {href ? (
          <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}
            style={{ color: theme.ink, textDecoration: 'none', fontSize: 15, lineHeight: 1.6 }}>
            {children}
          </a>
        ) : (
          <div style={{ color: theme.ink, fontSize: 15, lineHeight: 1.6 }}>{children}</div>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, required }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} style={inputStyle} />
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, letterSpacing: '0.15em', color: theme.gold, marginBottom: 8 };
const inputStyle = { width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 2, fontSize: 14, fontFamily: theme.font.body, background: theme.white, color: theme.ink, boxSizing: 'border-box', outline: 'none' };
